import { AfterViewInit, Directive, ElementRef, Inject, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { IOnboardingTipsService, State } from '../../services/onboarding-tips/onboarding-tips.interface';

@Directive({
  selector: '[gtdOnboardingTips]',
})
export class OnboardingTipsDirective implements AfterViewInit, OnDestroy {
  private pageName: string;
  private subscription: Subscription;

  constructor(private elementRef: ElementRef, @Inject('IOnboardingTipsService') private onboardingTipsService: IOnboardingTipsService) {
    this.subscription = this.onboardingTipsService.stateChanges().subscribe(() => {
      if (this.onboardingTipsService.getWorkingStatus()) {
        this.manageTips();
        return;
      }
      this.removeTips(this.elementRef.nativeElement);
    });
  }

  ngAfterViewInit() {
    if (this.onboardingTipsService.getWorkingStatus()) {
      this.manageTips();
      return;
    }
    this.removeTips(this.elementRef.nativeElement);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  manageTips() {
    const state: State = this.onboardingTipsService.getState();
    const nativeElement = this.elementRef.nativeElement;
    this.pageName = this.onboardingTipsService.getCurrentPageKey();
    const infoIcon = `<span class="p500-ui-icon-book-open-02"><span class="path1"></span><span class="path2"></span><span class="path3"></span></span>`;
    this.removeTips(nativeElement);

    switch (this.pageName) {
      case 'dashboard':
        if (state.dashboard.status && nativeElement.classList.contains('onboarding-tip__date')) {
          nativeElement.classList.add('onboarding-tip');
          nativeElement.insertAdjacentHTML(
            'beforeend',
            `<div class='onboarding-tip__info onboarding-tip--date'>
            ${infoIcon}
            ${$localize`:@@tips.dashboard_top_tip:You can select the period at the top right corner (by default it’s <b>“This Month”</b>)`}.<div>`
          );
        }

        this.highlightSidebarItem(nativeElement, '.p500-ui-icon-home');

        break;

      case 'profile':
        if (state.profile.status) {
          nativeElement.classList.add('onboarding-tip');
          nativeElement.insertAdjacentHTML(
            'beforeend',
            `<div class='onboarding-tip__info onboarding-tip__info--down onboarding-tip--two-fa'>
            ${infoIcon}
            ${$localize`:@@tips.profile_top_tip:Enable it and be sure your information is safe.`}<div>`
          );
          break;
        }
        nativeElement.classList.remove('onboarding-tip');
        break;

      case 'offers':
        if (state.offers.status && nativeElement.classList.contains('onboarding-tip--reportBuilder')) {
          nativeElement.classList.add('onboarding-tip');
          nativeElement.insertAdjacentHTML(
            'beforeend',
            `<div class='onboarding-tip__info onboarding-tip__info--left onboarding-tip--report-builder'>
            ${infoIcon}
            ${$localize`:@@tips.offers_top_tip:When a report is required, click the <b>Data Exporter</b> button. Here you can generate a <b>CSV file</b> with full and wide data for your needs.`}<div>`
          );
        }

        this.highlightSidebarItem(nativeElement, '.p500-ui-icon-tags');

        break;

      case 'conversions':
        if (state.conversions.status && nativeElement.classList.contains('onboarding-tip--reportBuilder')) {
          nativeElement.classList.add('onboarding-tip');
          nativeElement.insertAdjacentHTML(
            'beforeend',
            `<div class='onboarding-tip__info onboarding-tip__info--left onboarding-tip--report-builder-conversion'>
            ${infoIcon}
            ${$localize`:@@tips.conversions_top_tip:You can see all the information about conversions in this table, but if you’d like a systemized report, press the <b>Data Exporter</b> button.`}<div>`
          );
        }

        this.highlightSidebarItem(nativeElement, '.p500-ui-icon-funnel');

        break;

      case 'clicks':
        this.highlightSidebarItem(nativeElement, '.p500-ui-icon-click');

        break;

      case 'invoices':
        setTimeout(() => {
          const targetElement = nativeElement.querySelector('.c-widget__add-icon')?.parentElement;
          targetElement?.classList.add('onboarding-tip--create__wrapper');

          if (state.invoices.status && targetElement && this.pageName !== 'smartLinks') {
            targetElement.classList.add('onboarding-tip');
            targetElement.insertAdjacentHTML(
              'beforeend',
              `<div class='onboarding-tip__info onboarding-tip__info--right onboarding-tip--create'>
              ${infoIcon}
              ${$localize`:@@tips.invoices_top_tip:To create an invoice, press the <b>Create</b> button and fill in the required information.`}<div>`
            );
          }
        }, 1000);

        if (!this.onboardingTipsService.getWorkingStatus() || !state.invoices.status) {
          nativeElement.querySelector('.c-widget__add-icon')?.parentElement?.classList.remove('onboarding-tip');
        }

        this.highlightSidebarItem(nativeElement, '.p500-ui-icon-wallet');

        break;

      case 'affiliateTrackers':
        setTimeout(() => {
          const createButton = nativeElement.querySelector('.c-widget__add-icon')?.parentElement;
          createButton?.classList.add('onboarding-tip--create__wrapper');

          if (!state.affiliateTrackers.status) {
            return;
          }

          if (createButton && this.pageName !== 'smartLinks') {
            createButton.classList.add('onboarding-tip');
            createButton.insertAdjacentHTML(
              'beforeend',
              `<div class='onboarding-tip__info onboarding-tip__info--right onboarding-tip--create'>
                ${infoIcon}
                ${$localize`:@@tips.aff_trackers_top_tip:Press the <b>Create button</b> to add an affiliate tracker.`}<div>`
            );
          }

          if (nativeElement && nativeElement?.classList?.contains('c-table-header__filters')) {
            nativeElement.classList.add('onboarding-tip');
            nativeElement.insertAdjacentHTML(
              'beforeend',
              `<div class='onboarding-tip__info onboarding-tip--filter'>
                ${infoIcon}
                ${$localize`:@@tips.aff_trackers_top_tip1:This section shows only the active trackers by default. To reset, clear the <b>Activity</b> filter.`}<div>`
            );
          }

          if (nativeElement && nativeElement?.classList?.contains('p500-ui-new-table') && nativeElement.querySelectorAll('tr').length > 4) {
            nativeElement.querySelector('.p500-ui-table').style.position = 'static';
            const editButtonCells = nativeElement.querySelectorAll('tr')[3].querySelectorAll('td');
            const editButton = editButtonCells[editButtonCells.length - 1];

            editButton.classList.add('onboarding-tip');
            editButton.insertAdjacentHTML(
              'beforeend',
              `<div class='onboarding-tip__info onboarding-tip--edit'>
                ${infoIcon}
                ${$localize`:@@tips.aff_trackers_top_tip2:You can edit Affiliate Tracker either from the main list or inside of it by pressing the <b>“Edit”</b> button.`}<div>`
            );
          }
        }, 1000);

        if (!this.onboardingTipsService.getWorkingStatus() || !state.affiliateTrackers.status) {
          nativeElement.querySelector('.c-widget__add-icon')?.parentElement?.classList.remove('onboarding-tip');
          nativeElement.classList.remove('onboarding-tip');
          const editButtonCells = nativeElement?.querySelectorAll('tr')[3]?.querySelectorAll('td');
          if (editButtonCells) {
            editButtonCells[editButtonCells.length - 1].classList.remove('onboarding-tip');
          }
        }

        this.highlightSidebarItem(nativeElement, '.p500-ui-icon-flow');

        break;

      case 'reports':
        if (state.reports.status && state.reports.tips.create) {
          nativeElement.classList.add('onboarding-tip');
          nativeElement.insertAdjacentHTML(
            'beforeend',
            `<div class='onboarding-tip__info onboarding-tip__info--right onboarding-tip--reports'>
              ${infoIcon}
              ${$localize`:@@tips.reports_top_tip:There are three buttons specifying the type of data that will be collected: <b>Clicks</b>, <b>Conversions</b>, <b>Offers</b>. Click on a needed one.`}<div>`
          );
        }

        if (nativeElement.classList.contains('onboarding-p500-ui-sidenav')) {
          const offset = nativeElement?.querySelector('[data-title="Data Exporter"]')?.parentElement?.getBoundingClientRect().top;
          nativeElement?.querySelector('[data-title="Data Exporter"]')?.classList.add('onboarding-tip');
          this.onboardingTipsService.setOffsetTopModal(offset);
        }

        break;

      case 'smartLinks': {
        if (!this.onboardingTipsService.getWorkingStatus() && !state.smartLinks.status) {
          return;
        }

        const target = nativeElement.querySelector('.c-widget__add-icon')?.parentElement;
        target?.classList.add('onboarding-tip--create__smart-link');

        if (state.smartLinks.tips.create && target) {
          removeElements();
          target.classList.add('onboarding-tip');
        } else {
          removeCreate();
        }
        if (state.smartLinks.tips.actions && nativeElement.classList.contains('p500-ui-new-table')) {
          removeElements();
          nativeElement.querySelector('.p500-ui-table').style.position = 'static';
          nativeElement.querySelector('thead').style.zIndex = 'initial';
          nativeElement.querySelectorAll('thead th').forEach((th) => {
            th.style.zIndex = 'initial';
            th.style.position = 'relative';
          });

          const theadCells = nativeElement.querySelector('thead tr').querySelectorAll('th');
          const addHeader = theadCells[3];
          const deleteHeader = theadCells[4];
          const getLinkHeader = theadCells[5];
          addHeader.classList.add('onboarding-tip');
          deleteHeader.classList.add('onboarding-tip');
          getLinkHeader.classList.add('onboarding-tip');
        } else {
          removeActions();
        }

        if (state.smartLinks.tips.id && nativeElement.classList.contains('p500-ui-new-table')) {
          removeElements();
          nativeElement.querySelector('.p500-ui-table').style.position = 'static';
          const firstRowIdElement = nativeElement.querySelector('tbody tr')?.querySelector('td');
          firstRowIdElement?.classList.add('onboarding-tip');
        } else {
          removeId();
        }

        function removeCreate() {
          nativeElement.querySelector('.c-widget__add-icon')?.parentElement?.classList.remove('onboarding-tip');
        }

        function removeActions() {
          nativeElement.querySelector('thead tr')?.querySelectorAll('th')[3].classList.remove('onboarding-tip');
          nativeElement.querySelector('thead tr')?.querySelectorAll('th')[4].classList.remove('onboarding-tip');
          nativeElement.querySelector('thead tr')?.querySelectorAll('th')[5].classList.remove('onboarding-tip');
        }

        function removeId() {
          nativeElement.querySelector('tbody tr')?.querySelector('td').classList.remove('onboarding-tip');
        }

        function removeElements() {
          removeCreate();
          removeActions();
          removeId();
        }

        if (!this.onboardingTipsService.getWorkingStatus() || !state.smartLinks.status) {
          removeElements();
        }

        this.highlightSidebarItem(nativeElement, '.p500-ui-icon-link-01');

        break;
      }

      case 'offer':
        if (state.offer.status) {
          if (nativeElement.classList.contains('onboarding-tip__preview')) {
            nativeElement.classList.add('onboarding-tip');
            nativeElement.insertAdjacentHTML(
              'beforeend',
              `<div class='onboarding-tip__info onboarding-tip__info--down onboarding-tip--preview'>
                ${infoIcon}
                ${$localize`:@@tips.offer_top_tip:You can always <b>preview</b> the funnel before choosing it.`}<div>`
            );
          }
        }

        if (!this.onboardingTipsService.getWorkingStatus() || !state.offer.status) {
          nativeElement.classList.remove('onboarding-tip');
        }

        this.highlightSidebarItem(nativeElement, '.p500-ui-icon-tags');

        break;

      case 'drilldown': {
        this.onboardingTipsService.setOffsetRightModal(32);
        const groupSelector = nativeElement.querySelector('.onboarding-tip--drilldown-group');
        const settingsIcon = nativeElement.querySelector('.onboarding-tip--drilldown-settings');
        const conditions = nativeElement.querySelector('.onboarding-tip--drilldown-conditions');
        if (state.drilldown.tips.group) {
          groupSelector?.classList?.add('onboarding-tip');
        } else {
          groupSelector?.classList?.remove('onboarding-tip');
        }
        if (state.drilldown.tips.metrics) {
          settingsIcon?.classList?.add('onboarding-tip');
        } else {
          settingsIcon?.classList?.remove('onboarding-tip');
        }
        if (state.drilldown.tips.conditions) {
          conditions?.classList?.add('onboarding-tip');
        } else {
          conditions?.classList?.remove('onboarding-tip');
        }
        this.highlightSidebarItem(nativeElement, '.p500-ui-icon-chart-02');

        break;
      }
      case 'comparingAnalytics': {
        if (state.comparingAnalytics.tips.conditions) {
          nativeElement.classList.add('onboarding-tip');
        } else {
          nativeElement.classList.remove('onboarding-tip');
        }
        break;
      }
      default:
    }
  }

  removeTips(nativeElement) {
    nativeElement?.classList.remove('onboarding-tip');
    nativeElement?.querySelector('.p500-ui-icon-tags')?.parentElement.classList.remove('onboarding-tip');
    nativeElement?.querySelector('.p500-ui-icon-click')?.parentElement.classList.remove('onboarding-tip');
    nativeElement?.querySelector('.p500-ui-icon-home')?.parentElement.classList.remove('onboarding-tip');
    nativeElement?.querySelector('.p500-ui-icon-funnel')?.parentElement.classList.remove('onboarding-tip');
    nativeElement?.querySelector('.p500-ui-icon-flow')?.parentElement.classList.remove('onboarding-tip');
    nativeElement?.querySelector('.p500-ui-icon-link-01')?.parentElement.classList.remove('onboarding-tip');
    nativeElement?.querySelector('.p500-ui-icon-wallet')?.parentElement.classList.remove('onboarding-tip');
    nativeElement?.querySelector('.p500-ui-icon-chart-board')?.parentElement.classList.remove('onboarding-tip');
    nativeElement?.querySelector('.p500-ui-icon-chart-02')?.parentElement.classList.remove('onboarding-tip');
  }

  highlightSidebarItem(nativeElement, selector: string): void {
    if (nativeElement.classList.contains('onboarding-p500-ui-sidenav')) {
      const sidebarItem = nativeElement?.querySelector(selector)?.parentElement;
      // sidebarItem?.classList.add('onboarding-tip');
      this.onboardingTipsService.setOffsetTopModal(sidebarItem?.offsetTop);
    }
  }
}
